import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import {
  type ParsedReportHeatmapAssetPath,
  parseReportHeatmapAssetPath,
} from 'pages/reporting/reports/rug/utils/reportHeatmapStorage'
import { useCallback, useMemo } from 'react'

export interface ReportHeatmapAsset {
  componentId: ComponentId
  sectionKey?: string
  descriptorHash: string
  bundlePath: string
  heatmapPath: string
  heatmapSignedUrl?: string
  legendPath?: string
  legendSignedUrl?: string
  descriptorPath?: string
  imagePath: string
  signedUrl?: string
}

export interface AssetScreenshots {
  fullComponentHeatmap?: ReportHeatmapAsset[]
  sectionHeatmap?: ReportHeatmapAsset[]
  fullComponentDrawing?: {
    componentId: ComponentId
    imagePath: string
    signedUrl?: string
    extension: string
  }[]
}

interface AssetDrawingUrlResult {
  path?: string
  signed_url?: string
}

function createEmptyReportHeatmapAsset(
  parsedPath: ParsedReportHeatmapAssetPath
): ReportHeatmapAsset {
  return {
    componentId: parsedPath.componentId,
    sectionKey: parsedPath.sectionKey,
    descriptorHash: parsedPath.descriptorHash,
    bundlePath: parsedPath.bundlePath,
    heatmapPath: '',
    imagePath: '',
    signedUrl: undefined,
  }
}

function getReportHeatmapAssetPatch(
  parsedPath: ParsedReportHeatmapAssetPath,
  result: AssetDrawingUrlResult
): Partial<ReportHeatmapAsset> {
  switch (parsedPath.fileName) {
    case 'heatmap.png':
      return {
        heatmapPath: result.path ?? '',
        heatmapSignedUrl: result.signed_url,
        imagePath: result.path ?? '',
        signedUrl: result.signed_url,
      }
    case 'legend.png':
      return {
        legendPath: result.path,
        legendSignedUrl: result.signed_url,
      }
    case 'descriptor.json':
      return {
        descriptorPath: result.path,
      }
  }
}

export const useAssetScreenshots = (metadataEntryId: string) => {
  const { fileRefs: screenshotRefs, forceRefresh: forceRefreshScreenshotRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'asset-screenshot/**',
    })

  const { fileRefs: drawingRefs, forceRefresh: forceRefreshDrawingRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'asset-drawing/**',
    })

  const allFileRefs = useMemo(
    () => [...screenshotRefs, ...drawingRefs],
    [screenshotRefs, drawingRefs]
  )

  const {
    results: assetDrawingUrls,
    forceRefresh: forceRefreshAssetDrawingUrls,
  } = useRolodexSignedURLs(
    allFileRefs.map((ref) => ({
      entry_id: metadataEntryId,
      path: ref.path,
    }))
  )

  const assetDrawings = useMemo(() => {
    const drawings: AssetScreenshots = {
      fullComponentHeatmap: [],
      sectionHeatmap: [],
      fullComponentDrawing: [],
    }
    const reportHeatmapBundles = new Map<string, ReportHeatmapAsset>()

    assetDrawingUrls.forEach((result) => {
      const parsedPath = parseReportHeatmapAssetPath(result.path)

      if (parsedPath) {
        const existingBundle =
          reportHeatmapBundles.get(parsedPath.bundlePath) ??
          createEmptyReportHeatmapAsset(parsedPath)

        reportHeatmapBundles.set(parsedPath.bundlePath, {
          ...existingBundle,
          ...getReportHeatmapAssetPatch(parsedPath, result),
        })
      } else if (result.path?.includes('fullComponentDrawing')) {
        // Extract component ID and extension from path: asset-drawing/fullComponentDrawing/<componentId>.<extension>
        const match = result.path.match(/fullComponentDrawing\/(.+)\.(\w+)$/)
        if (match) {
          const componentId = match[1] as ComponentId
          const extension = match[2]
          drawings.fullComponentDrawing?.push({
            componentId,
            imagePath: result.path,
            signedUrl: result.signed_url,
            extension,
          })
        }
      }
    })

    reportHeatmapBundles.forEach((bundle) => {
      if (!bundle.heatmapPath) return

      if (bundle.sectionKey) {
        drawings.sectionHeatmap?.push(bundle)
      } else {
        drawings.fullComponentHeatmap?.push(bundle)
      }
    })

    return Object.keys(drawings).length > 0 ? drawings : undefined
  }, [assetDrawingUrls])

  const forceRefresh = useCallback(async () => {
    await forceRefreshScreenshotRefs()
    await forceRefreshDrawingRefs()
    await forceRefreshAssetDrawingUrls()
  }, [
    forceRefreshScreenshotRefs,
    forceRefreshDrawingRefs,
    forceRefreshAssetDrawingUrls,
  ])

  return {
    assetScreenshots: assetDrawings,
    forceRefresh,
  }
}
